import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, Spinner, cn, type Tone } from "../../../../components/ui";
import { daysUntil, dueOf, fmtDate, isOverdue } from "../../lib";
import { MODE_TONES, OVERDUE_TONE, STATUS_ICONS, STATUS_TONES, TYPE_ICONS } from "../../theme";
import type { MaintenanceRecord } from "../../types";
import { useMxLabels } from "../../hooks/useMxLabels";

// ---------------------------------------------------------------------------
// Piezas de la consola operativa (estilo iVMS): marco de página con barra de
// herramientas, paneles, KPIs, conmutador de vista, chips de estado y textos
// de vencimiento. Sólo tokens del tema (claro y oscuro).
// ---------------------------------------------------------------------------

export interface PageShellProps {
  icon: string;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Barra de herramientas bajo el título (búsqueda, filtros, vista). */
  toolbar?: ReactNode;
  children: ReactNode;
  /** Sin padding en el contenido (layouts de dos paneles). */
  flush?: boolean;
}

export function PageShell({ icon, title, subtitle, actions, toolbar, children, flush = false }: PageShellProps) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-app">
      <div className="shrink-0 border-b border-line bg-surface px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
              <i className={cn(icon, "text-xl")} />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-content">{title}</h1>
              {subtitle ? <p className="truncate text-xs text-content-secondary">{subtitle}</p> : null}
            </div>
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {toolbar ? <div className="mt-3 flex flex-wrap items-center gap-2">{toolbar}</div> : null}
      </div>
      <div className={cn("min-h-0 flex-1 overflow-y-auto", flush ? "" : "p-4 sm:p-6")}>
        {flush ? children : <div className="mx-auto w-full max-w-[1600px]">{children}</div>}
      </div>
    </div>
  );
}

export interface PanelProps {
  title?: ReactNode;
  icon?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function Panel({ title, icon, actions, children, className, bodyClassName }: PanelProps) {
  return (
    <section className={cn("flex min-w-0 flex-col rounded-xl border border-line bg-card", className)}>
      {title || actions ? (
        <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <h3 className="flex min-w-0 items-center gap-2 truncate text-sm font-semibold text-content">
            {icon ? <i className={cn(icon, "text-content-muted")} /> : null}
            {title}
          </h3>
          {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn("min-h-0 flex-1 p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

const KPI_TONES = {
  neutral: "bg-subtle text-content-secondary",
  primary: "bg-primary-500/15 text-primary-500",
  success: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
  error: "bg-error/15 text-error",
  info: "bg-info/15 text-info",
} as const satisfies Record<Tone, string>;

export interface KpiCardProps {
  icon: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  loading?: boolean;
  onClick?: () => void;
  /** 0-100: barra de progreso bajo el valor. */
  progress?: number;
}

export function KpiCard({ icon, label, value, hint, tone = "primary", loading, onClick, progress }: KpiCardProps) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-card p-4 text-left",
        onClick ? "transition-colors hover:border-primary-500/50 hover:bg-subtle/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" : ""
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium uppercase tracking-wide text-content-muted">{label}</span>
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", KPI_TONES[tone])}>
          <i className={cn(icon, "text-base")} />
        </span>
      </div>
      <div className="text-2xl font-bold tabular-nums text-content">{loading ? <span className="inline-block h-7 w-16 animate-pulse rounded bg-subtle" /> : value}</div>
      {progress !== undefined && !loading ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-subtle">
          <div className={cn("h-full rounded-full", progress >= 85 ? "bg-success" : progress >= 70 ? "bg-warning" : "bg-error")} style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
      ) : null}
      {hint ? <p className="truncate text-xs text-content-secondary">{hint}</p> : null}
    </Tag>
  );
}

export interface ViewOption<T extends string> {
  id: T;
  icon: string;
  label: string;
}

export function ViewSwitch<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: ViewOption<T>[] }) {
  return (
    <div className="flex shrink-0 rounded-lg border border-line bg-card p-0.5" role="group">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          title={o.label}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
            value === o.id ? "bg-primary-500 text-on-primary" : "text-content-secondary hover:bg-subtle hover:text-content"
          )}
        >
          <i className={o.icon} />
          <span className="hidden md:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string }) {
  return (
    <label className={cn("flex h-9 min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-line bg-card px-3 sm:max-w-xs", className)}>
      <i className="ri-search-line text-content-muted" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full bg-transparent text-sm text-content outline-none placeholder:text-content-faint"
      />
      {value ? (
        <button type="button" onClick={() => onChange("")} className="text-content-muted hover:text-content" aria-label="×">
          <i className="ri-close-line" />
        </button>
      ) : null}
    </label>
  );
}

export function SelectFilter({ value, onChange, options, label, className }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; label: string; className?: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={cn("h-9 max-w-[220px] rounded-lg border border-line bg-card px-2.5 text-sm text-content outline-none focus:border-primary-500", className)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function ToolbarButton({ icon, label, onClick, primary, disabled, spinning }: { icon: string; label: string; onClick: () => void; primary?: boolean; disabled?: boolean; spinning?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        "inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors disabled:opacity-50",
        primary ? "bg-primary-500 text-on-primary hover:bg-primary-400" : "border border-line bg-card text-content-secondary hover:bg-subtle hover:text-content"
      )}
    >
      <i className={cn(icon, spinning ? "animate-spin" : "")} />
      <span className={primary ? "whitespace-nowrap" : "hidden whitespace-nowrap lg:inline"}>{label}</span>
    </button>
  );
}

/** Estado del registro; si está vencido, lo dice antes que el estado. */
export function StatusBadge({ record, size = "md", onPhoto = false }: { record: MaintenanceRecord; size?: "sm" | "md"; /** Sobre una foto: variante sólida para que se lea. */ onPhoto?: boolean }) {
  const L = useMxLabels();
  const status = record.status || "pendiente";
  if (isOverdue(record)) {
    return (
      <Badge tone={OVERDUE_TONE} size={size} variant={onPhoto ? "solid" : "soft"} dot>
        {L.status("overdue")}
      </Badge>
    );
  }
  return (
    <Badge tone={STATUS_TONES[status as keyof typeof STATUS_TONES] ?? "neutral"} variant={onPhoto || status === "en_proceso" ? "solid" : "soft"} size={size}>
      <i className={STATUS_ICONS[status] ?? "ri-question-line"} />
      {L.status(status)}
    </Badge>
  );
}

export function TypeBadge({ type, size = "sm" }: { type?: string | null; size?: "sm" | "md" }) {
  const L = useMxLabels();
  if (!type) return null;
  return (
    <Badge tone="neutral" variant="outline" size={size}>
      <i className={TYPE_ICONS[type] ?? "ri-tools-line"} />
      {L.type(type)}
    </Badge>
  );
}

export function ModeBadge({ mode, size = "sm" }: { mode?: string | null; size?: "sm" | "md" }) {
  const L = useMxLabels();
  const m = mode === "externo" ? "externo" : "propio";
  return (
    <Badge tone={MODE_TONES[m]} size={size}>
      <i className={m === "externo" ? "ri-store-2-line" : "ri-home-gear-line"} />
      {L.mode(m)}
    </Badge>
  );
}

/** «Venció hace 3 días» / «Vence en 2 días» / «Completado 12 oct». */
export function DueText({ record, className }: { record: MaintenanceRecord; className?: string }) {
  const { t } = useTranslation("common");
  const L = useMxLabels();
  if (record.status === "completado") {
    return <span className={cn("text-xs text-content-secondary", className)}>{t("mantenimiento.ui.due.completedOn", { date: fmtDate(record.completedDate || record.closedAt, L.locale) })}</span>;
  }
  const due = dueOf(record);
  const d = daysUntil(due);
  if (d === null) return <span className={cn("text-xs text-content-muted", className)}>{t("mantenimiento.ui.due.none")}</span>;
  const overdue = isOverdue(record);
  const text = overdue ? t("mantenimiento.ui.due.overdueBy", { count: -d }) : d === 0 ? t("mantenimiento.ui.due.today") : d > 0 ? t("mantenimiento.ui.due.in", { count: d }) : fmtDate(due, L.locale);
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", overdue ? "font-semibold text-error" : d <= 2 ? "font-medium text-warning" : "text-content-secondary", className)} title={fmtDate(due, L.locale, true)}>
      <i className={overdue ? "ri-alarm-warning-line" : "ri-calendar-line"} />
      {text}
    </span>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-content-muted">
      <Spinner size="sm" />
      {label ?? t("mantenimiento.loading")}
    </div>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation("common");
  return (
    <EmptyState
      icon="ri-error-warning-line"
      title={t("mantenimiento.ui.errorTitle")}
      description={message}
      action={
        onRetry ? (
          <button type="button" onClick={onRetry} className="rounded-lg border border-line px-3 py-1.5 text-sm text-content hover:bg-subtle">
            <i className="ri-refresh-line mr-1" />
            {t("mantenimiento.ui.retry")}
          </button>
        ) : undefined
      }
    />
  );
}

export function IconAction({ icon, label, onClick, className }: { icon: string; label: string; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn("rounded-md p-1.5 text-sm transition-colors", className ?? "text-content-secondary hover:bg-subtle hover:text-content")}
    >
      <i className={icon} />
    </button>
  );
}

/** Aviso visible cuando el API trajo menos registros de los que hay. */
export function TruncationNotice({ shown, total, onLoadMore, loading }: { shown: number; total: number; onLoadMore?: () => void; loading?: boolean }) {
  const { t } = useTranslation("common");
  if (total <= shown) return null;
  return (
    <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-content">
      <i className="ri-information-line text-warning" />
      <span className="flex-1">{t("mantenimiento.ui.truncated", { shown, total })}</span>
      {onLoadMore ? (
        <button type="button" onClick={onLoadMore} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1 text-xs font-medium text-content hover:bg-subtle disabled:opacity-50">
          <i className={loading ? "ri-loader-4-line animate-spin" : "ri-arrow-down-line"} />
          {t("mantenimiento.ui.loadMore")}
        </button>
      ) : null}
    </div>
  );
}
